import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import JsBarcode from "jsbarcode";
import { api, errMsg, fmtMoney, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Printer, BadgeCheck } from "lucide-react";

const PRINT_CSS = `
.print-show { display: none; }
@page { margin: 7mm; }
@media print {
  body { background: #ffffff !important; color: #000000 !important; }
  .no-print, footer { display: none !important; }
  .print-show { display: flex !important; }
  .receipt-shell { padding: 0 !important; margin: 0 !important; max-width: 100% !important; }
  .print-area { box-shadow: none !important; border: none !important; border-radius: 0 !important; padding: 0 !important; background: #ffffff !important; width: 100% !important; max-width: 100% !important; margin: 0 !important; font-size: 11px !important; }
  .print-content { padding: 2px 4px !important; }
  .print-area * { color: #000000 !important; }
  .print-area .font-display { font-size: 18px !important; }
  .print-area [data-testid="receipt-invoice-title"] { font-size: 26px !important; }
  .print-area .text-3xl, .print-area .sm\\:text-4xl { font-size: 18px !important; }
  .print-area .text-sm { font-size: 11px !important; }
  .print-area .text-xs { font-size: 9.5px !important; }
  .print-area img { max-height: 64px !important; width: auto !important; }
  .print-area svg { max-height: 22px !important; }
  .print-area [data-testid="receipt-booking-barcode"] { max-height: none !important; max-width: none !important; height: 52px !important; width: 210px !important; }
  .print-area .h-2 { height: 4px !important; }
  .print-area .p-6 { padding: 8px !important; }
  .print-area .px-4 { padding-left: 10px !important; padding-right: 10px !important; }
  .print-area .py-4 { padding-top: 5px !important; padding-bottom: 5px !important; }
  .print-area .py-3\\.5 { padding-top: 5px !important; padding-bottom: 5px !important; }
  .print-area .mb-10 { margin-bottom: 8px !important; }
  .print-area .mb-8 { margin-bottom: 6px !important; }
  .print-area .mt-12 { margin-top: 8px !important; }
  .print-area .mt-10 { margin-top: 6px !important; }
  .print-area .mt-4 { margin-top: 5px !important; }
  .print-area .pt-8 { padding-top: 6px !important; }
  .print-area .pb-8 { padding-bottom: 6px !important; }
  .print-area .gap-6, .print-area .gap-8 { gap: 10px !important; }
  .avoid-break { page-break-inside: avoid; }
}
`;

const PostRentalBadge = () => (
  <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-ui font-medium bg-rose-100 text-rose-800 border border-rose-200 ml-2 align-middle">post-rental</span>
);

export default function Receipt() {
  const { bookingId } = useParams();
  const { user } = useAuth();
  const [b, setB] = useState(null);
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [loyalty, setLoyalty] = useState(null);
  const [reviewUrl, setReviewUrl] = useState("");
  const barcodeRef = useRef(null);

  useEffect(() => {
    if (b?.booking_ref && barcodeRef.current) {
      try {
        JsBarcode(barcodeRef.current, b.booking_ref, {
          format: "CODE128",
          displayValue: false,
          height: 40,
          width: 1.6,
          background: "transparent",
          lineColor: "#1c1917",
          margin: 0,
        });
      } catch (e) {}
    }
    if (b && new URLSearchParams(window.location.search).get("print") === "1") {
      const t = setTimeout(() => window.print(), 700);
      return () => clearTimeout(t);
    }
  }, [b]);

  const load = (emailToUse) => {
    const stored = localStorage.getItem(`booking_email_${bookingId}`) || "";
    const em = emailToUse ?? stored;
    api
      .get(`/bookings/${bookingId}`, { params: em ? { email: em } : {} })
      .then((r) => {
        setB(r.data);
        setError("");
        api.get("/loyalty/status", { params: { email: r.data.customer_email } }).then((lr) => setLoyalty(lr.data)).catch(() => {});
        api.get("/review-link").then((rl) => setReviewUrl(rl.data?.url || "")).catch(() => {});
      })
      .catch((e) => setError(errMsg(e)));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  if (error && !b)
    return (
      <div data-testid="receipt-access-panel" className="max-w-md mx-auto px-6 pt-40 pb-24">
        <p className="text-[11px] font-ui tracking-[0.3em] uppercase text-primary">Easy Drive Car Rental</p>
        <h1 className="mt-3 font-display text-4xl tracking-tight">Rental <span className="italic gold-text">Invoice</span></h1>
        <p className="mt-4 text-sm text-muted-foreground">Enter the email used for this booking to view the invoice.</p>
        {error && <p className="mt-3 text-sm text-destructive font-ui">{error}</p>}
        <div className="mt-6 flex gap-3">
          <Input data-testid="receipt-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="input-dark" onKeyDown={(e) => e.key === "Enter" && load(email)} />
          <Button data-testid="receipt-load-button" onClick={() => load(email)} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui shrink-0">
            View
          </Button>
        </div>
      </div>
    );

  if (!b) return <div className="pt-40 pb-24 text-center text-muted-foreground font-ui">Loading invoice…</div>;

  const base = b.days * b.daily_rate;
  const paid = b.status === "confirmed" || b.status === "completed";
  const postCharges = (Number(b.pi_toll_amount) || 0) + (Number(b.smoking_fee) || 0) + (Number(b.cleaning_fee) || 0) + (Number(b.fuel_charge) || 0) + (Number(b.late_fee) || 0) + (Number(b.airport_return_fee) || 0) + (Number(b.key_fob_fee) || 0);
  const weeklySav = Number(b.weekly_discount) || 0;
  const couponSav = Number(b.discount_amount) || 0;
  const buddySav = Number(b.vehicle_daily_rate) > Number(b.daily_rate) ? (Number(b.vehicle_daily_rate) - Number(b.daily_rate)) * b.days : 0;
  const totalSavings = weeklySav + couponSav + buddySav;
  const savingsParts = [
    weeklySav ? `Weekly 7+ day rate −${fmtMoney(weeklySav)}` : null,
    couponSav ? `${b.loyalty_tier ? `Loyalty ${b.loyalty_tier}` : `Code ${b.coupon_code}`} −${fmtMoney(couponSav)}` : null,
    buddySav ? `Special rate ${fmtMoney(b.daily_rate)}/day (reg. ${fmtMoney(b.vehicle_daily_rate)}) −${fmtMoney(buddySav)}` : null,
  ].filter(Boolean);
  const rows = [
    [`Rental — ${b.days} ${b.days === 1 ? "day" : "days"} × ${fmtMoney(b.daily_rate)}`, base, false],
    ["Third-Party Liability Insurance — included", 0, false],
    b.baby_seat ? ["Baby / child car seat", b.baby_seat_fee, false] : null,
    b.roadside ? ["24/7 roadside assistance", b.roadside_fee || 0, false] : null,
    b.gps ? ["GPS navigation unit", b.gps_fee, false] : null,
    b.sli ? ["Supplementary Liability Insurance (SLI) — $500 deductible", b.sli_fee || 0, false] : null,
    b.additional_drivers ? [`Additional drivers × ${b.additional_drivers}`, b.additional_driver_fee, false] : null,
    b.weekly_discount ? ["Weekly discount — 7+ days (−10%)", -b.weekly_discount, false] : null,
    b.hour_extensions?.length ? [`Extra hours — ${b.hour_extensions.reduce((s, h) => s + (h.hours || 0), 0)} hr @ $25/hr`, b.hour_extensions.reduce((s, h) => s + (h.amount || 0), 0), false] : null,
    b.discount_amount ? [`${b.loyalty_tier ? `Loyalty — ${b.loyalty_tier}${loyalty?.member_no ? ` · Member ${loyalty.member_no}` : ""}` : `Discount code ${b.coupon_code}`} (−${b.discount_percent}%)`, -b.discount_amount, false] : null,
    Number(b.vat_amount) > 0 ? ["VAT (10%)", b.vat_amount, false] : null,
    b.vat_exempt && !(Number(b.vat_amount) > 0) ? ["VAT (10%) — exempt, not charged", 0, false] : null,
    ["Processing fee (5%)", b.processing_fee || 0, false],
    b.pi_toll_amount ? [`Paradise Island bridge toll${b.pi_toll_note ? ` — ${b.pi_toll_note}` : ""}`, b.pi_toll_amount, true] : null,
    Number(b.smoking_fee) > 0 ? ["Smoking penalty", b.smoking_fee, true] : null,
    Number(b.cleaning_fee) > 0 ? ["Cleaning fee", b.cleaning_fee, true] : null,
    Number(b.fuel_charge) > 0 ? [`Fuel refill — ${b.fuel_gallons} gal @ $8.00/gal`, b.fuel_charge, true] : null,
    Number(b.late_fee) > 0 ? [`Late return — ${b.late_hours} hr @ $25/hr`, b.late_fee, true] : null,
    Number(b.airport_return_fee) > 0 ? ["Airport return fee", b.airport_return_fee, true] : null,
    Number(b.key_fob_fee) > 0 ? ["Key fob replacement", b.key_fob_fee, true] : null,
  ].filter(Boolean);

  return (
    <div data-testid="receipt-page" className="receipt-shell max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-24">
      <style>{PRINT_CSS}</style>
      <div className="no-print mb-6 flex flex-wrap items-center justify-between gap-4 p-4 bg-white/80 backdrop-blur-md rounded-2xl border border-stone-200 shadow-sm">
        <Link to={user?.role === "admin" ? "/admin" : "/account"} className="text-sm font-ui text-stone-500 hover:text-primary transition-colors duration-200">
          ← Back
        </Link>
        <Button data-testid="receipt-print-button" variant="outline" onClick={() => window.print()} className="rounded-full border-stone-300 bg-white hover:bg-stone-50 hover:text-primary font-ui transition-colors">
          <Printer size={15} className="mr-2" /> Print / Save PDF
        </Button>
      </div>

      <div className="print-area bg-white rounded-2xl shadow-[0_12px_40px_rgba(0,0,0,0.06)] border border-stone-200/80 overflow-hidden">
        <div className="h-2 bg-gradient-to-r from-[#C71E2E] via-[#8F1420] to-[#C71E2E]" />
        <div className="print-content p-6 sm:p-10 lg:p-12">
          {/* Letterhead */}
          <div className="grid grid-cols-12 gap-6 items-start border-b border-stone-200 pb-8 mb-8">
            <div className="col-span-7">
              <img src="/logo.png" alt="Easy Drive Car Rental" data-testid="receipt-logo" className="h-20 w-auto object-contain" />
              <div className="mt-4 text-xs text-stone-500 font-ui leading-relaxed tracking-wide">
                <p>West Bay Street · Nassau, Bahamas</p>
                <p>+1 (242) 813-1775 · bookings@easydrivecarrental.com</p>
                <p>www.easydrivecarrental.com</p>
              </div>
            </div>
            <div className="col-span-5 text-right">
              <p data-testid="receipt-invoice-title" className="font-display text-3xl sm:text-4xl tracking-[0.1em] text-stone-900">INVOICE</p>
              <p data-testid="receipt-ref" className="mt-2 font-mono text-sm font-semibold text-stone-700">INV-{b.booking_ref}</p>
              <p className="mt-1 text-xs text-stone-500 font-ui">{new Date(b.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</p>
              <div className="mt-3 flex justify-end">
                {paid ? (
                  <span data-testid="receipt-paid-badge" className="inline-flex items-center gap-1.5 text-[10px] font-ui font-semibold tracking-[0.2em] uppercase text-emerald-800 border border-emerald-300 bg-emerald-50 rounded-full px-3 py-1">
                    <BadgeCheck size={11} /> Paid
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-[10px] font-ui font-semibold tracking-[0.2em] uppercase text-amber-800 border border-amber-300 bg-amber-50 rounded-full px-3 py-1">
                    Pending
                  </span>
                )}
              </div>
              <div className="mt-4 flex flex-col items-end">
                <svg ref={barcodeRef} data-testid="receipt-booking-barcode" className="max-w-full" />
                <p className="mt-1 text-[9px] font-ui tracking-[0.25em] uppercase text-stone-400">Scan at check-in / rental return</p>
              </div>
            </div>
          </div>

          {/* Meta grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mb-10 p-6 bg-stone-50/80 rounded-xl border border-stone-200/60 text-sm">
            <div data-testid="invoice-billed-to-card">
              <p className="font-ui text-[10px] tracking-[0.25em] uppercase text-stone-400 border-b border-amber-200/60 pb-2 mb-3">Billed To</p>
              <p className="font-semibold text-stone-900">{b.customer_name}</p>
              <p className="text-stone-500 mt-1">{b.customer_email}</p>
              <p className="text-stone-500">{b.customer_phone}</p>
              {loyalty?.member_no && (
                <p data-testid="receipt-loyalty-no" className="text-stone-500 mt-1">Loyalty No. <span className="gold-text font-medium">{loyalty.member_no}</span>{loyalty.percent ? ` · ${loyalty.tier}` : ""}</p>
              )}
            </div>
            <div data-testid="invoice-rental-details-card">
              <p className="font-ui text-[10px] tracking-[0.25em] uppercase text-stone-400 border-b border-amber-200/60 pb-2 mb-3">Rental Details</p>
              <p className="font-semibold text-stone-900">{b.vehicle_name}</p>
              <p className="text-stone-500 mt-1">
                {fmtDate(b.start_date)}{b.pickup_time ? ` · ${b.pickup_time}` : ""} →<br />{fmtDate(b.end_date)}{b.dropoff_time ? ` · ${b.dropoff_time}` : ""}
              </p>
              <p className="text-stone-500">{b.pickup_location}</p>
              <p data-testid="receipt-fuel-level" className="text-stone-500 mt-1">Fuel at pickup: <span className="text-stone-800 font-medium">{b.fuel_level || "Full"}</span> — return at same level</p>
              {b.vehicle_seats && (
                <p data-testid="receipt-vehicle-specs" className="text-stone-500 mt-1">{b.vehicle_seats} seats · Fits {b.vehicle_luggage ?? 0} bags</p>
              )}
            </div>
            <div data-testid="invoice-payment-details-card">
              <p className="font-ui text-[10px] tracking-[0.25em] uppercase text-stone-400 border-b border-amber-200/60 pb-2 mb-3">Payment</p>
              <p className="capitalize font-semibold text-stone-900">{b.payment_method || "—"}</p>
              <p className="text-stone-500 mt-1">{b.paid_at ? `Paid ${new Date(b.paid_at).toLocaleString()}` : "Awaiting payment"}</p>
              <p className="text-stone-500">Deposit: <span className="capitalize">{b.deposit_status || "held"}</span></p>
            </div>
          </div>

          {/* Line items */}
          <div className="avoid-break mb-10">
            <div className="grid grid-cols-[1fr_auto] px-4 py-3.5 bg-stone-100/90 border-b-2 border-stone-300 rounded-t-lg font-ui text-[10px] tracking-[0.25em] uppercase text-stone-600">
              <span>Description</span>
              <span className="text-right">Amount (USD)</span>
            </div>
            <div data-testid="invoice-line-items-table" className="border border-stone-200 border-t-0 rounded-b-lg overflow-hidden">
              {rows.map(([label, amount, isPost], i) => (
                <div key={i} className="grid grid-cols-[1fr_auto] px-4 py-4 text-sm border-b border-stone-100 last:border-b-0 hover:bg-amber-50/30 transition-colors duration-150">
                  <span className="text-stone-800 font-ui font-medium">
                    {label}
                    {isPost && <PostRentalBadge />}
                  </span>
                  <span className={`font-mono text-right ${amount < 0 ? "text-emerald-700" : "text-stone-900"} font-medium`}>{amount < 0 ? "−" : ""}{fmtMoney(Math.abs(amount))}</span>
                </div>
              ))}
            </div>
          </div>

          {totalSavings > 0 ? (
            <div data-testid="invoice-savings-banner" className="avoid-break mb-10 rounded-xl border border-emerald-300 bg-emerald-50 px-5 py-4 flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="font-ui font-semibold text-emerald-900 text-sm">You saved {fmtMoney(totalSavings)} vs. standard pricing</p>
                <p data-testid="invoice-savings-breakdown" className="text-xs font-ui text-emerald-800 mt-0.5">{savingsParts.join(" · ")}</p>
              </div>
              <span className="font-display text-2xl text-emerald-700">−{fmtMoney(totalSavings)}</span>
            </div>
          ) : (
            <div data-testid="invoice-savings-banner" className="avoid-break mb-10 rounded-xl border border-stone-200 bg-stone-50 px-5 py-3 flex flex-wrap items-center justify-end gap-2">
              <span className="font-ui text-sm text-stone-500">Savings: $0.00</span>
            </div>
          )}

          {/* Totals */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start mb-10">
            <div className="hidden lg:block lg:col-span-6" />
            <div className="lg:col-span-6 space-y-1 bg-stone-50/50 p-6 rounded-xl border border-stone-200/80 avoid-break">
              <div className="flex items-center justify-between text-sm py-1.5 border-b border-stone-200/50 font-ui text-stone-600">
                <span>Rental total</span>
                <span className="font-mono">{fmtMoney(b.total)}</span>
              </div>
              <div className="flex items-center justify-between text-sm py-1.5 border-b border-stone-200/50 font-ui text-stone-600">
                <span>Refundable deposit ({b.deposit_status || "held"})</span>
                <span className="font-mono">{fmtMoney(b.deposit_amount || 150)}</span>
              </div>
              {b.deposit_status === "charged" && Array.isArray(b.deposit_breakdown) && b.deposit_breakdown.length > 0 && (
                <div data-testid="receipt-deposit-breakdown" className="rounded-lg border border-stone-200/80 bg-white/70 p-3.5 my-1.5">
                  <p className="text-[10px] font-ui uppercase tracking-[0.2em] text-stone-400 mb-1.5">Deposit applied to charges</p>
                  {b.deposit_breakdown.map((it, i) => (
                    <div key={i} data-testid={`receipt-deposit-line-${i}`} className="flex items-center justify-between text-xs py-0.5 font-ui text-stone-600">
                      <span>{it.label}{it.note ? ` — ${it.note}` : ""}</span>
                      <span className="font-mono">{fmtMoney(it.amount)}</span>
                    </div>
                  ))}
                  <div className="flex items-center justify-between text-xs py-1 font-ui font-semibold text-stone-800 border-t border-stone-200/70 mt-1">
                    <span>Applied from deposit</span>
                    <span data-testid="receipt-deposit-applied" className="font-mono">{fmtMoney(b.deposit_applied_amount || 0)}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs py-0.5 font-ui text-emerald-700">
                    <span>Released back to you</span>
                    <span data-testid="receipt-deposit-released-back" className="font-mono">{fmtMoney(b.deposit_released_back || 0)}</span>
                  </div>
                </div>
              )}
              {Number(b.pi_toll_amount) > 0 && (
                <div data-testid="receipt-toll-row" className="flex items-center justify-between text-sm py-1.5 border-b border-stone-200/50 font-ui text-stone-600">
                  <span>PI bridge toll <PostRentalBadge /></span>
                  <span className="font-mono">{fmtMoney(b.pi_toll_amount)}</span>
                </div>
              )}
              {Number(b.smoking_fee) > 0 && (
                <div data-testid="receipt-smoking-row" className="flex items-center justify-between text-sm py-1.5 border-b border-stone-200/50 font-ui text-stone-600">
                  <span>Smoking penalty <PostRentalBadge /></span>
                  <span className="font-mono">{fmtMoney(b.smoking_fee)}</span>
                </div>
              )}
              {Number(b.cleaning_fee) > 0 && (
                <div data-testid="receipt-cleaning-row" className="flex items-center justify-between text-sm py-1.5 border-b border-stone-200/50 font-ui text-stone-600">
                  <span>Cleaning fee <PostRentalBadge /></span>
                  <span className="font-mono">{fmtMoney(b.cleaning_fee)}</span>
                </div>
              )}
              {Number(b.fuel_charge) > 0 && (
                <div data-testid="receipt-fuel-row" className="flex items-center justify-between text-sm py-1.5 border-b border-stone-200/50 font-ui text-stone-600">
                  <span>Fuel refill — {b.fuel_gallons} gal @ $8.00/gal <PostRentalBadge /></span>
                  <span className="font-mono">{fmtMoney(b.fuel_charge)}</span>
                </div>
              )}
              {Number(b.late_fee) > 0 && (
                <div data-testid="receipt-late-row" className="flex items-center justify-between text-sm py-1.5 border-b border-stone-200/50 font-ui text-stone-600">
                  <span>Late return — {b.late_hours} hr @ $25/hr <PostRentalBadge /></span>
                  <span className="font-mono">{fmtMoney(b.late_fee)}</span>
                </div>
              )}
              {Number(b.airport_return_fee) > 0 && (
                <div data-testid="receipt-airport-return-row" className="flex items-center justify-between text-sm py-1.5 border-b border-stone-200/50 font-ui text-stone-600">
                  <span>Airport return fee <PostRentalBadge /></span>
                  <span className="font-mono">{fmtMoney(b.airport_return_fee)}</span>
                </div>
              )}
              {Number(b.key_fob_fee) > 0 && (
                <div data-testid="receipt-key-fob-row" className="flex items-center justify-between text-sm py-1.5 border-b border-stone-200/50 font-ui text-stone-600">
                  <span>Key fob replacement <PostRentalBadge /></span>
                  <span className="font-mono">{fmtMoney(b.key_fob_fee)}</span>
                </div>
              )}
              <div className="flex items-center justify-between pt-4 pb-1 border-t-2 border-amber-300">
                <span className="font-ui font-semibold text-stone-900">{paid ? "Amount charged" : "Amount due"}</span>
                <span data-testid="receipt-total" className="gold-text font-display text-3xl sm:text-4xl tracking-tight">{fmtMoney((b.amount_due || b.total) + postCharges)}</span>
              </div>
              {postCharges > 0 && (
                Number(b.charges_paid_amount) >= postCharges - 0.001 ? (
                  <p data-testid="receipt-charges-paid" className="text-xs font-ui text-emerald-700 text-right pt-1">Post-rental charges paid ✓</p>
                ) : (
                  <p className="text-right pt-1">
                    <Link data-testid="receipt-pay-charges-link" to={`/pay-charges/${b.id}`} className="text-xs font-ui text-primary underline underline-offset-2">Pay post-rental charges online →</Link>
                  </p>
                )
              )}
            </div>
          </div>

          {b.extensions?.length > 0 && (
            <div data-testid="invoice-extensions-section" className="mb-10 p-6 bg-stone-50/80 rounded-xl border border-stone-200/60 text-sm text-stone-600 font-ui avoid-break">
              <p className="uppercase tracking-[0.25em] mb-3 text-[10px] text-stone-400 border-b border-amber-200/60 pb-2">Extensions — same booking, updated total</p>
              {b.extensions.map((x, i) => (
                <p key={i} className="mt-1.5">Extended to {fmtDate(x.new_end_date)} — {fmtMoney(x.amount)} paid · {new Date(x.paid_at).toLocaleDateString()} · no late fees</p>
              ))}
            </div>
          )}

          {/* Legal + signature */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end pt-8 border-t border-stone-200 avoid-break">
            <p className="md:col-span-7 text-[11px] text-stone-500 font-ui leading-relaxed">
              Cancellations within 24 hours of pickup are non-refundable. The security
              deposit is released once the vehicle is returned in good condition. All
              charges are subject to 10% Bahamas VAT and a 5% processing fee.
            </p>
            <div className="md:col-span-5 text-center">
              <div className="w-44 mx-auto border-t border-stone-400 pt-2 mt-10 text-[10px] font-ui tracking-[0.2em] uppercase text-stone-500">
                Authorized Signature
              </div>
            </div>
          </div>
          {loyalty && (
            <div data-testid="receipt-loyalty-progress" className="mb-8 p-5 rounded-xl border border-amber-200/70 bg-amber-50/50 text-center avoid-break">
              {loyalty.member !== false ? (
                Number(loyalty.rentals_to_next) > 0 ? (
                  <p className="text-sm font-ui text-stone-700">
                    You're <b>{loyalty.rentals_to_next} rental{loyalty.rentals_to_next === 1 ? "" : "s"}</b> away from <b className="text-[#C71E2E]">{loyalty.next_tier}</b> — your next discount unlocks automatically. <span className="text-stone-500">Member {loyalty.member_no} · {loyalty.rentals} completed rental{loyalty.rentals === 1 ? "" : "s"}.</span>
                  </p>
                ) : (
                  <p className="text-sm font-ui text-stone-700">You're at our top tier, <b className="text-[#C71E2E]">{loyalty.tier}</b> — thank you for driving with us. <span className="text-stone-500">Member {loyalty.member_no}.</span></p>
                )
              ) : (
                <p className="text-sm font-ui text-stone-700">Join <b className="text-[#C71E2E]">Easy Drive Loyalty</b> free — every completed rental moves you up a tier and discounts apply automatically. <Link to="/loyalty" className="text-primary underline underline-offset-2">Join in seconds →</Link></p>
              )}
            </div>
          )}

          {b.status === "completed" && reviewUrl && (
            <div data-testid="receipt-review-cta" className="no-print mb-8 p-5 rounded-xl border border-stone-200 bg-white text-center avoid-break">
              <p className="font-display text-lg tracking-tight text-stone-800">How was your rental?</p>
              <p className="mt-1 text-xs font-ui text-stone-500">A 30-second Google review helps our family business more than you know.</p>
              <div className="mt-3 flex flex-wrap items-center justify-center gap-3">
                <a data-testid="receipt-google-review-link" href={reviewUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-[#C71E2E] text-white font-ui text-xs font-semibold px-5 py-2.5 hover:bg-[#A01825] transition-colors">
                  Leave a Google review
                </a>
                <a data-testid="receipt-feedback-link" href="https://wa.me/12428131775?text=Hi%20Easy%20Drive%20—%20feedback%20on%20my%20rental%20" className="text-xs font-ui text-stone-500 underline underline-offset-2 hover:text-stone-700">Had an issue? Tell us first</a>
              </div>
            </div>
          )}

          <p className="mt-12 text-center font-display text-lg tracking-tight text-stone-800">
            Thank you for choosing <span className="italic gold-text">Easy Drive Car Rental</span>
          </p>
        </div>
      </div>
    </div>
  );
}
